/**
 * Rótulo del período de una respuesta de Meta Ads (`period: { since, until }`,
 * `YYYY-MM-DD`). Nunca un texto fijo: lo decide la API (default 30 días
 * **incluido hoy**, hora de Córdoba; el día de hoy es parcial).
 */

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Fecha de hoy en Córdoba como `YYYY-MM-DD`. */
export function metaTodayCordoba(now = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Argentina/Cordoba', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

const parseDay = (value) => {
  const m = ISO_DAY.exec(String(value || '').trim());
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(m[1]), month, day: Number(m[3]) };
};

const dayLabel = (d, withYear) => `${d.day} ${MONTHS[d.month - 1]}${withYear ? ` ${d.year}` : ''}`;

/**
 * `{ since: '2026-09-23', until: '2026-10-22' }` → «23 sep – 22 oct (hoy, parcial)».
 * Año solo si el período no es del año actual o cruza años. Sin período válido → ''.
 *
 * @param {{ since?: string, until?: string }|null} period
 * @param {Date} [now]
 */
export function formatMetaPeriod(period, now = new Date()) {
  const since = parseDay(period?.since);
  const until = parseDay(period?.until);
  if (!since || !until) return '';
  const today = metaTodayCordoba(now);
  const currentYear = Number(today.slice(0, 4));
  const withYear = since.year !== until.year || until.year !== currentYear;
  const range = period.since === period.until
    ? dayLabel(until, withYear)
    : `${dayLabel(since, withYear)} – ${dayLabel(until, withYear)}`;
  return String(period.until).trim() === today ? `${range} (hoy, parcial)` : range;
}

/**
 * «Gasto total del período (hoy, parcial): ARS 114.150», sin fechas: en el panel ya están en el
 * selector de período. Solo queda la marca de parcial si el rango termina hoy. Sin gasto → ''.
 */
/** ¿El período termina hoy (datos parciales)? */
export function metaPeriodIsPartial(period, now = new Date()) {
  return String(period?.until || '').trim() === metaTodayCordoba(now);
}

export function metaSpendTotalLine(period, spend, now = new Date(), { prefix = 'Gasto' } = {}) {
  if (!spend) return '';
  const partial = metaPeriodIsPartial(period, now);
  return `${prefix}${partial ? ' (hoy, parcial)' : ''}: ${spend}`;
}

/**
 * «Gasto 23 sep – 22 oct (hoy, parcial): ARS 114.150». Sin gasto: solo el período.
 */
export function metaPeriodSpendLine(period, spend, now = new Date(), { prefix = 'Gasto' } = {}) {
  const label = formatMetaPeriod(period, now);
  if (!spend) return label;
  return label ? `${prefix} ${label}: ${spend}` : `${prefix}: ${spend}`;
}

/**
 * Fecha y hora (ISO con zona) en Córdoba: «22 oct 09:00» (año si no es el actual). Inválida → ''.
 */
export function formatMetaDateTime(iso, now = new Date()) {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  try {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'America/Argentina/Cordoba', year: 'numeric', month: 'numeric', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(date).map((p) => [p.type, p.value]));
    const year = Number(parts.year);
    const withYear = year !== Number(metaTodayCordoba(now).slice(0, 4));
    return `${Number(parts.day)} ${MONTHS[Number(parts.month) - 1]}${withYear ? ` ${year}` : ''} ${parts.hour}:${parts.minute}`;
  } catch {
    return '';
  }
}

/** ¿El período es solo «hoy» (día de Córdoba)? Hoy los desgloses de público pueden estar incompletos. */
export function isMetaTodayPeriod(period, now = new Date()) {
  const today = metaTodayCordoba(now);
  return Boolean(period) && period.since === today && period.until === today;
}

/** `YYYY-MM-DD` (o ISO con hora: se usa el día que trae) → «14 ago» / «30 nov 2026». Inválida → ''. */
export function formatMetaDay(value, { withYear = false } = {}) {
  const d = parseDay(String(value || '').trim().slice(0, 10));
  return d ? dayLabel(d, withYear) : '';
}

/**
 * Selector de período como el Administrador de anuncios (ajuste 10, solo Front).
 * Cada preset → `{ since, until }` (`YYYY-MM-DD`, días de Córdoba) para
 * `buildMetaPeriodParams` (getmetacampaigns, getcampaignmetrics y, cuando haya
 * servicio, getmetaresults). La API acepta hasta 400 días.
 */
export const META_PERIOD_PRESETS = [
  { key: 'today', label: 'Hoy' },
  { key: 'yesterday', label: 'Ayer' },
  { key: 'last_7d', label: 'Últimos 7 días' },
  { key: 'last_30d', label: 'Últimos 30 días' },
  { key: 'this_month', label: 'Este mes' },
  { key: 'maximum', label: 'Máximo (90 días)' },
];
export const META_DEFAULT_PERIOD_PRESET = 'last_30d';
export const META_MAX_PERIOD_DAYS = 400;

/** `YYYY-MM-DD` + n días (aritmética de calendario, sin zona). Inválida → ''. */
export function addMetaDays(iso, n) {
  const d = parseDay(iso);
  if (!d) return '';
  const date = new Date(Date.UTC(d.year, d.month - 1, d.day + n));
  return date.toISOString().slice(0, 10);
}

/**
 * Rango de un preset con «hoy» de Córdoba:
 * - Hoy: hoy–hoy · Ayer: ayer–ayer
 * - Últimos 7 / 30 días: hoy−7 / hoy−30 a ayer (sin hoy, como Meta)
 * - Este mes: día 1 a hoy
 * - Máximo (90 días): hoy−89 a hoy = lo mismo que `days=90` de la API (los 90 días que guarda la sync)
 * Preset desconocido → el default (últimos 30 días).
 *
 * @param {string} key
 * @param {Date} [now]
 * @returns {{ since: string, until: string }}
 */
export function metaPeriodPresetRange(key, now = new Date()) {
  const today = metaTodayCordoba(now);
  const yesterday = addMetaDays(today, -1);
  switch (key) {
    case 'today': return { since: today, until: today };
    case 'yesterday': return { since: yesterday, until: yesterday };
    case 'last_7d': return { since: addMetaDays(today, -7), until: yesterday };
    case 'this_month': return { since: `${today.slice(0, 8)}01`, until: today };
    case 'maximum': return { since: addMetaDays(today, -89), until: today };
    case 'last_30d':
    default:
      return { since: addMetaDays(today, -30), until: yesterday };
  }
}

/** ¿Es un preset conocido? */
export const isMetaPeriodPreset = (key) => META_PERIOD_PRESETS.some((p) => p.key === key);
